// Sentry integration for v2.
// Ports vanilla's window.Sentry setup (public/app/index.html L33-177):
//   - DSN (L33), init (L87), setUser (L62), setTag (L65-67)
//   - _captureErr (L37), _bc breadcrumb (L45), _updateSentryContext (L55)

import * as Sentry from '@sentry/react'
import { env } from '../env'
import type { User } from 'firebase/auth'
import type { Role } from '../types'

let _initialized = false

export function initSentry(): void {
  if (_initialized) return
  if (!env.sentryDsn) {
    // In dev without a DSN, be quiet — Sentry does no harm if uninitialized.
    if (env.isDev) console.info('[sentry] no DSN set, skipping init (dev mode)')
    return
  }
  Sentry.init({
    dsn: env.sentryDsn,
    release: env.appVersion,
    environment: env.isProd ? 'production' : 'development',
    sampleRate: 1,
    tracesSampleRate: 0,
    ignoreErrors: [
      'ResizeObserver loop',
      'Non-Error promise rejection captured',
    ],
    beforeSend(event) {
      // Strip PII: email, ?token= query param, request headers.
      // Mirrors vanilla's L87 beforeSend behavior.
      if (event.user?.email) delete event.user.email
      if (event.request?.url) {
        event.request.url = event.request.url.replace(/([?&])token=[^&]+/g, '$1token=__redacted__')
      }
      return event
    },
  })
  _initialized = true
}

// Captured error — replaces vanilla window._captureErr.
export function captureErr(err: unknown, context?: Record<string, unknown>): void {
  if (context) Sentry.setContext('jsp', context)
  Sentry.captureException(err)
  if (env.isDev) console.error('[captureErr]', err, context)
}

// Breadcrumb — replaces vanilla window._bc.
export function breadcrumb(category: string, message: string, data?: Record<string, unknown>): void {
  Sentry.addBreadcrumb({
    category,
    message,
    level: 'info',
    ...(data ? { data } : {}),
  })
}

// Called on auth state change + role change. Mirrors vanilla _updateSentryContext.
export function setSentryUser(user: User | null, role: Role, extras?: {
  teamCode?: string
  companyType?: string
}): void {
  if (!user) {
    Sentry.setUser(null)
    Sentry.setTag('role', 'anon')
    return
  }
  Sentry.setUser({ id: user.uid })  // no email — privacy
  Sentry.setTag('role', role)
  Sentry.setTag('teamCode', extras?.teamCode ?? 'none')
  Sentry.setTag('companyType', extras?.companyType ?? 'none')
}

// Exported for use in error boundaries (ErrorBoundary component in Week 2).
export { Sentry }
